/**
 * Koffer-Übersicht (Devices)
 * @module Devices
 */

import SecuredZone from '../components/SecuredZone';
import DeviceDialog from '../components/dialogs/device-dialog/device-dialog';
import API from '../api/api';
import Dialog from '@mui/material/Dialog';
import LinearProgress from '@mui/material/LinearProgress';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import { Icon, IconWell } from '../icons/AppIcons';
import { calculateDeviceMetrics, validateDeviceData } from '../utils/deviceHelpers';
import { handleApiError } from '../utils/errorHandler';

const FireApi = new API();

class Devices extends Component {
  constructor(props) {
    super(props);
    this.state = {
      devices: [],
      loading: true,
      dialogOpen: false,
      page: 0,
      rowsPerPage: 10,
      totalCount: 0,
    };
  }

  openDialogDevice() {
    this.setState({ dialogOpen: true });
  }

  closeDialogDevice() {
    this.setState({ dialogOpen: false });
  }

  submitDialogDevice(a) {
    this.setState({ dialogOpen: false, loading: true });
    FireApi.endpoints.devices.create(a.device)
      .then(() => {
        this.setState({ loading: false });
        this.loadData();
      })
      .catch((error) => {
        handleApiError(error, 'beim Erstellen des Koffers', () => {
          this.setState({ loading: false });
        });
      });
  }

  loadData(page = null, rowsPerPage = null) {
    const currentPage = page !== null ? page : this.state.page;
    const currentRowsPerPage = rowsPerPage !== null ? rowsPerPage : this.state.rowsPerPage;

    FireApi.endpoints.devicesStatus.getAll(
      { withCredentials: true, crossDomain: true },
      { page: currentPage + 1, page_size: currentRowsPerPage }
    )
      .then((res) => {
        try {
          let validated = [];
          let totalCount = 0;

          if (res?.data) {
            if (res.data.results && Array.isArray(res.data.results)) {
              validated = validateDeviceData(res.data.results);
              totalCount = res.data.count || res.data.results.length;
            } else if (Array.isArray(res.data)) {
              validated = validateDeviceData(res.data);
              totalCount = res.data.length;
            }
          }

          this.setState({ devices: validated, totalCount, loading: false });
        } catch (e) {
          console.error('Fehler beim Verarbeiten der Koffer-Daten:', e);
          this.setState({ devices: [], totalCount: 0, loading: false });
        }
      })
      .catch((error) => {
        handleApiError(error, 'beim Laden der Koffer', () => {
          this.setState({ devices: [], totalCount: 0, loading: false });
        });
      });
  }

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage }, () => this.loadData(newPage, null));
  };

  handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    this.setState({ rowsPerPage: newRowsPerPage, page: 0 }, () => this.loadData(0, newRowsPerPage));
  };

  componentDidMount() {
    this.loadData();
  }

  render() {
    return (
      <SecuredZone
        title="Koffer"
        breadcrumbs={{ '/': 'Dashboard', '/devices': 'Koffer' }}
        menu={{ Hinzufügen: this.openDialogDevice.bind(this) }}
        loading={this.state.loading ? <LinearProgress color="primary" /> : <span className="loader-span"></span>}
      >
        <Dialog open={this.state.dialogOpen} onClose={this.closeDialogDevice} aria-labelledby="device-dialog-title">
          <DeviceDialog close={this.closeDialogDevice.bind(this)} submit={this.submitDialogDevice.bind(this)} />
        </Dialog>

        <div className="overflow-visibile">
          <TableContainer style={{ overflow: 'visibile' }} className="settings-table" component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Aktiv</TableCell>
                  <TableCell align="left">Name</TableCell>
                  <TableCell align="left">UUID</TableCell>
                  <TableCell align="left">Temperatur</TableCell>
                  <TableCell align="left">Signal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(this.state.devices) && this.state.devices.length > 0 ? (
                  this.state.devices.map((device) => {
                    const metrics = calculateDeviceMetrics(device);
                    return (
                      <TableRow
                        style={{ textDecoration: 'none' }}
                        key={device.uuid || device.id}
                        component={Link}
                        to={`/devices/${device.id}/`}
                        role="button"
                        tabIndex={0}
                        aria-label={`Koffer ${device.name || device.id} öffnen`}
                      >
                        <TableCell align="left">
                          {metrics.isOnline ? (
                            <span className="fc-chip is-ok">
                              <Icon name="online" size={18} /> Online
                            </span>
                          ) : (
                            <span className="fc-chip is-alarm">
                              <Icon name="offline" size={18} /> Offline
                            </span>
                          )}
                        </TableCell>
                        <TableCell align="left">
                          {device.active ? (
                            <span className="fc-chip is-ok">
                              <Icon name="ok" size={18} /> Aktiv
                            </span>
                          ) : (
                            <span className="fc-chip is-mute">
                              <Icon name="disconnected" size={18} /> Inaktiv
                            </span>
                          )}
                        </TableCell>
                        <TableCell align="left">
                          <span className="fc-name-cell">
                            <IconWell name="koffer" tone="accent" size={22} />
                            {device.name || '-'}
                          </span>
                        </TableCell>
                        <TableCell align="left">{device.uuid || '-'}</TableCell>
                        <TableCell align="left">
                          <span className="fc-name-cell">
                            <Icon name="temperature" size={18} />
                            {metrics.temperature} °C
                          </span>
                        </TableCell>
                        <TableCell align="left">
                          <div className="signal-quality-waves-small">
                            <div className={`waveStrength-small-${metrics.qualityCalc}`}>
                              <div className="wv4 wave-small">
                                <div className="wv3 wave-small">
                                  <div className="wv2 wave-small">
                                    <div className="wv1 wave-small" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" style={{ padding: '60px 20px' }}>
                      {this.state.loading ? (
                        <div>Lade Koffer...</div>
                      ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Icon name="koffer" size={64} sx={{ opacity: 0.4, mb: 1 }} />
                            <div>Keine Koffer gefunden</div>
                          </div>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={this.state.totalCount}
            page={this.state.page}
            onPageChange={this.handleChangePage}
            rowsPerPage={this.state.rowsPerPage}
            onRowsPerPageChange={this.handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            labelRowsPerPage="Zeilen pro Seite:"
            labelDisplayedRows={({ from, to, count, page }) => {
              const totalPages = count > 0 ? Math.ceil(count / this.state.rowsPerPage) : 1;
              return `Seite ${page + 1} von ${totalPages}`;
            }}
            className="table-pagination"
          />
        </div>
      </SecuredZone>
    );
  }
}

Devices.propTypes = {
  history: PropTypes.object.isRequired,
  match: PropTypes.object,
};

export default Devices;
